import React, { useState } from 'react';

interface AccountData {
  accountName: string;
  accountNumber: string;
  balance?: string;
}

interface AccountSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAccount: (accountNumber: string) => void;
  accounts: AccountData[];
  transactionAmount: number;
  bankName: string;
  isLoading?: boolean;
}

const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectAccount,
  accounts,
  transactionAmount,
  bankName,
  isLoading = false
}) => {
  const [selectedAccount, setSelectedAccount] = useState<string>('');

  const handleSelectAccount = () => {
    if (selectedAccount) {
      onSelectAccount(selectedAccount);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatBalance = (balance: string): string => {
    // Remove any non-numeric characters except decimal point
    const numericBalance = balance.replace(/[^\d.-]/g, '');
    const balanceNumber = parseFloat(numericBalance);
    
    if (isNaN(balanceNumber)) {
      return balance; // Return original if can't parse
    }
    
    return formatCurrency(balanceNumber);
  };

  const isBalanceSufficient = (balance: string): boolean => {
    const numericBalance = balance.replace(/[^\d.-]/g, '');
    const balanceNumber = parseFloat(numericBalance);
    return !isNaN(balanceNumber) && balanceNumber >= transactionAmount;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Select Account</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Choose account to transfer from
            </h3>
            <p className="text-gray-600">
              Select the {bankName} account you want to transfer {formatCurrency(transactionAmount)} from:
            </p>
          </div>

          {/* Account List */}
          <div className="space-y-3 mb-6">
            {accounts.map((account, index) => {
              const hasSufficientBalance = account.balance ? isBalanceSufficient(account.balance) : true;
              
              return (
                <div
                  key={index}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedAccount === account.accountNumber
                      ? 'border-blue-500 bg-blue-50'
                      : hasSufficientBalance
                      ? 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      : 'border-red-200 bg-red-50 cursor-not-allowed opacity-60'
                  }`}
                  onClick={() => {
                    if (hasSufficientBalance) {
                      setSelectedAccount(account.accountNumber);
                    }
                  }}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="account"
                      value={account.accountNumber}
                      checked={selectedAccount === account.accountNumber}
                      onChange={() => {
                        if (hasSufficientBalance) {
                          setSelectedAccount(account.accountNumber);
                        }
                      }}
                      disabled={!hasSufficientBalance}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            {account.accountName}
                          </h4>
                          <p className="text-sm text-gray-600 font-mono">
                            {account.accountNumber}
                          </p>
                        </div>
                        {account.balance && (
                          <div className="text-right">
                            <p className={`text-sm font-medium ${
                              hasSufficientBalance ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatBalance(account.balance)}
                            </p>
                            {!hasSufficientBalance && (
                              <p className="text-xs text-red-500 mt-1">
                                Insufficient balance
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {accounts.length === 0 && (
            <div className="text-center py-8">
              <div className="text-gray-400 text-lg mb-2">Loading accounts...</div>
              <p className="text-gray-500">
                Please wait while we retrieve your account information.
              </p>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mt-4"></div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSelectAccount}
              disabled={!selectedAccount || isLoading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md font-medium"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                'Confirm Selection'
              )}
            </button>
          </div>

          {/* Info Notice */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-blue-400">ℹ️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Account Selection</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    After selecting an account, the transfer will be processed automatically. 
                    Please ensure you have sufficient balance in the selected account.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSelectionModal;
